import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { getVaultKey, hasVaultKey } from "@/hooks/useKeyVault";
import {
  Activity,
  AlertTriangle,
  BrainCircuit,
  CheckCircle,
  ChevronRight,
  Clock,
  Download,
  FileDown,
  KeyRound,
  Loader2,
  Mail,
  Printer,
  RotateCcw,
  Search,
  Send,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Helmet } from "react-helmet-async";
import { downloadMarkdown, downloadReportPdf } from "@/lib/reportExport";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import {
  AnalyticsStreamHttpError,
  isRetryableAnalyticsJobFailure,
  readAnalyticsJobStream,
  type AnalyticsJobFailure,
} from "@/lib/analyticsJobStream";
import {
  ACTIVE_ANALYSIS_PROVIDERS,
  analysisProviderLabel,
  type AnalysisProvider,
} from "@shared/analysisProviders";
import type { ResearchValidation } from "@shared/researchEvidence";
import ResearchEvidenceDisplay from "@/components/ResearchEvidenceDisplay";
import ResearchDossierEntry from "@/components/ResearchDossierEntry";
import EventEvidenceReviewEntry from "@/components/EventEvidenceReviewEntry";
import ExposureReactionDossierEntry from "@/components/ExposureReactionDossierEntry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type JobPhase = "idle" | "analyzing" | "complete" | "error";
type TtmaProvider = AnalysisProvider;
type TradfiProvider = AnalysisProvider;
type InstitutionalResearchType = "tradfi" | "digital-assets";
type PromptMode = "guest" | "short" | "long";
type AnalyticsLane = "digital-asset-trader" | "digital-assets" | "tradfi";
type BitgetCatalogLane = AnalyticsLane | "all";
type BitgetCatalogStatus = "supported" | "excluded" | "all";
type BitgetCatalogAssetClass = BitgetAssetClass | "all";
type BitgetAssetClass = "crypto" | "stablecoin" | "metal" | "commodity" | "tokenized_stock" | "etf" | "pre_ipo" | "market_index" | "rwa" | "unknown";

interface AnalysisState {
  phase: JobPhase;
  asset: string | null;
  markdown: string | null;
  errorMsg: string | null;
  ttmaAvailable: boolean | null;
  provider: TtmaProvider;
  resolvedProvider: TtmaProvider | null;
  requestedModel: string | null;
  resolvedModel: string | null;
  promptMode: PromptMode;
  reportId: string | null;
  jobId: string | null;
  progress: string[];
}

interface GeminiAnalysisState {
  phase: JobPhase;
  asset: string | null;
  analysis: string | null;
  errorMsg: string | null;
  geminiAvailable: boolean | null;
  provider: TradfiProvider;
  requestedProvider: TradfiProvider | null;
  requestedModel: string | null;
  model: string | null;
  grounded: boolean;
  sources: Array<{ uri: string; title: string }>;
  researchValidation: ResearchValidation | null;
  marketSnapshot: unknown | null;
  reportId: string | null;
  researchType: InstitutionalResearchType;
  promptMode: PromptMode;
}

type AssetSuggestionSource = "bitget" | "yahoo";

interface BitgetAssetSuggestion {
  source: AssetSuggestionSource;
  symbol: string;
  baseCoin: string;
  quoteCoin: string;
  displaySymbol: string;
  productType: string;
  productLabel: string;
  status: string;
  isRwa: boolean;
  assetClass: BitgetAssetClass;
  analyticsLanes: AnalyticsLane[];
  aliases: string[];
  warnings: string[];
  analysisSymbol?: string;
  name?: string;
  exchange?: string;
}

interface BitgetSearchResponse {
  source: "bitget" | "research";
  providers?: Array<"bitget" | "yahoo">;
  createdAt: string;
  count: number;
  results: BitgetAssetSuggestion[];
  warnings?: string[];
}

interface BitgetCatalogRow extends BitgetAssetSuggestion {
  supported: boolean;
  minTradeUSDT?: string;
  launchTime?: string;
  endpoint: string;
}

interface BitgetCatalogResponse {
  source: "bitget" | "research";
  providers?: Array<"bitget" | "yahoo">;
  createdAt: string;
  status: BitgetCatalogStatus;
  supportedTotal: number;
  excludedTotal: number;
  discoveredTotal: number;
  total: number;
  count: number;
  offset: number;
  limit: number;
  results: BitgetCatalogRow[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getQuerySymbol(search: string): string {
  const params = new URLSearchParams(search);
  return (params.get("symbol") || "BTC").toUpperCase();
}

async function checkTtmaStatus(): Promise<boolean> {
  try {
    const res = await fetch("/api/ttma/status");
    const data = await res.json();
    return data.available === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const KeyStatusBadge: React.FC<{ hasKey: boolean }> = ({ hasKey }) => (
  <Link
    href="/get-started"
    className={`analytics-key-badge ${hasKey ? "analytics-key-badge--ok" : "analytics-key-badge--missing"}`}
    aria-label={hasKey ? "LLM provider key saved — click to manage keys" : "No LLM provider key — click to add one"}
  >
    <KeyRound size={13} aria-hidden="true" />
    <span>{hasKey ? "LLM KEY SAVED" : "NO LLM KEY"}</span>
    <ChevronRight size={12} aria-hidden="true" />
  </Link>
);

function providerLabel(provider: TtmaProvider | TradfiProvider) {
  return analysisProviderLabel(provider).toUpperCase();
}

function providerOptionLabel(provider: TtmaProvider | TradfiProvider) {
  if (provider === "gemini") return "Gemini — Search grounding unavailable on the free tier";
  if (provider === "groq") return "Groq — GPT-OSS 120B primary";
  return analysisProviderLabel(provider);
}

const ProviderBadge: React.FC<{ provider: TtmaProvider | TradfiProvider }> = ({ provider }) => {
  return <span className="analytics-gemini-badge">{providerLabel(provider)}</span>;
};

function promptModeLabel(mode: PromptMode) {
  if (mode === "guest") return "GUEST";
  if (mode === "short") return "SHORT";
  return "LONG";
}

function promptModeSummary(mode: PromptMode) {
  if (mode === "guest") return "Compact preview using shared guest capacity. No BYOK LLM key is sent.";
  if (mode === "short") return "Default BYOK mode. Uses your selected provider key with a smaller prompt.";
  return "Deep BYOK mode. Uses your selected provider key with the full prompt and more provider quota.";
}

function cleanAssetInput(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9.^=\-]/g, "").slice(0, 32);
}

function assetClassLabel(assetClass: BitgetAssetClass) {
  const labels: Record<BitgetAssetClass, string> = {
    crypto: "Crypto",
    stablecoin: "Stablecoin",
    metal: "Metal",
    commodity: "Commodity",
    tokenized_stock: "Stock",
    etf: "ETF",
    pre_ipo: "Pre-IPO",
    market_index: "Index",
    rwa: "RWA",
    unknown: "Listed",
  };
  return labels[assetClass];
}

function providerName(source: AssetSuggestionSource) {
  return source === "yahoo" ? "Yahoo" : "Bitget";
}

function laneLabel(lane: AnalyticsLane) {
  if (lane === "digital-asset-trader") return "Digital Asset Trader";
  if (lane === "digital-assets") return "Digital Assets Research";
  return "TradFi Research";
}

function catalogLaneLabel(lane: BitgetCatalogLane) {
  return lane === "all" ? "All lanes" : laneLabel(lane);
}

function catalogStatusLabel(status: BitgetCatalogStatus) {
  if (status === "supported") return "Supported";
  if (status === "excluded") return "Excluded";
  return "All";
}

function unsupportedAssetMessage(symbol: string, lane: AnalyticsLane) {
  if (lane === "digital-asset-trader") {
    return `${symbol} is not in Bitget's current trader catalog. Choose a listed crypto, token, or stablecoin instrument.`;
  }
  if (lane === "digital-assets") {
    return `${symbol} was not found in the Digital Assets Research universe. Try BTC, ETH, ETH-USD, GOLD, XAUT, PAXG, or a Bitget-listed digital asset.`;
  }
  return `${symbol} was not found in the TradFi Research universe. Try a Yahoo ticker such as NVDA, F, SPY, GOLD, SPX, GC=F, or a Bitget-listed tokenized/RWA asset.`;
}

function isExactAssetMatch(asset: BitgetAssetSuggestion, query: string) {
  const cleaned = cleanAssetInput(query);
  return asset.symbol === cleaned ||
    asset.baseCoin === cleaned ||
    asset.aliases.some((alias) => cleanAssetInput(alias) === cleaned);
}

async function resolveSupportedAssetForLane(value: string, lane: AnalyticsLane): Promise<BitgetAssetSuggestion> {
  const query = cleanAssetInput(value || "BTC");
  const params = new URLSearchParams({ q: query, lane, limit: "25" });
  const endpoint = lane === "digital-asset-trader" ? "/api/assets/bitget/search" : "/api/assets/research/search";
  const res = await fetch(`${endpoint}?${params.toString()}`);
  const data = await res.json() as Partial<BitgetSearchResponse> & { message?: string };
  if (!res.ok) {
    throw new Error(data.message || "Asset search is unavailable.");
  }
  const exact = (data.results ?? []).find((asset) => isExactAssetMatch(asset, query));
  if (!exact) {
    throw new Error(unsupportedAssetMessage(query, lane));
  }
  return exact;
}

const PromptModeSelect: React.FC<{
  id: string;
  value: PromptMode;
  onChange: (value: PromptMode) => void;
  disabled: boolean;
}> = ({ id, value, onChange, disabled }) => (
  <select
    id={id}
    className="analytics-provider-select"
    value={value}
    onChange={(e) => onChange(e.target.value as PromptMode)}
    disabled={disabled}
  >
    <option value="guest">Guest compact</option>
    <option value="short">BYOK short form</option>
    <option value="long">BYOK long form</option>
  </select>
);

const AssetSearchInput: React.FC<{
  id: string;
  value: string;
  lane: AnalyticsLane;
  disabled: boolean;
  onChange: (value: string) => void;
  onSelect: (asset: BitgetAssetSuggestion) => void;
}> = ({ id, value, lane, disabled, onChange, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BitgetAssetSuggestion[]>([]);
  const listId = `${id}-results`;

  useEffect(() => {
    if (!open || !value.trim()) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ q: value.trim(), lane, limit: "8" });
        const endpoint = lane === "digital-asset-trader" ? "/api/assets/bitget/search" : "/api/assets/research/search";
        const res = await fetch(`${endpoint}?${params.toString()}`, { signal: controller.signal });
        const data = await res.json() as Partial<BitgetSearchResponse> & { message?: string };
        if (!res.ok) throw new Error(data.message || "Asset search unavailable");
        setResults(data.results ?? []);
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          setResults([]);
          setError(err?.message ?? "Asset search unavailable");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [lane, open, value]);

  const chooseAsset = (asset: BitgetAssetSuggestion) => {
    onSelect(asset);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="analytics-asset-search">
      <Search size={14} className="analytics-asset-search-icon" aria-hidden="true" />
      <input
        id={id}
        value={value}
        onChange={(e) => {
          onChange(cleanAssetInput(e.target.value));
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        autoComplete="off"
        inputMode="text"
        maxLength={24}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
      />
      {open && value.trim() && (
        <div className="analytics-asset-menu" id={listId} role="listbox" aria-label={`${laneLabel(lane)} assets`}>
          {loading && <div className="analytics-asset-empty">Searching assets…</div>}
          {!loading && error && <div className="analytics-asset-empty">{error}</div>}
          {!loading && !error && results.length === 0 && (
            <div className="analytics-asset-empty">No supported match for {cleanAssetInput(value)}</div>
          )}
          {!loading && !error && results.map((asset) => (
            <button
              key={`${asset.symbol}-${asset.productType}-${asset.assetClass}`}
              type="button"
              className="analytics-asset-option"
              role="option"
              aria-selected={asset.baseCoin === cleanAssetInput(value) || asset.symbol === cleanAssetInput(value)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseAsset(asset)}
            >
              <span className="analytics-asset-symbol">{asset.analysisSymbol ?? asset.baseCoin}</span>
              <span className="analytics-asset-meta">
                {asset.name ? `${asset.name} · ` : ""}{asset.symbol} · {asset.productLabel}
              </span>
              <span className={`analytics-asset-class analytics-asset-class--${asset.assetClass}`}>
                {asset.source === "yahoo" ? "Yahoo" : assetClassLabel(asset.assetClass)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const catalogAssetClassOptions: BitgetCatalogAssetClass[] = [
  "all",
  "crypto",
  "stablecoin",
  "metal",
  "commodity",
  "tokenized_stock",
  "etf",
  "pre_ipo",
  "market_index",
  "rwa",
  "unknown",
];

function catalogRowFromSuggestion(asset: BitgetAssetSuggestion): BitgetCatalogRow {
  return {
    ...asset,
    supported: true,
    endpoint: asset.source,
    status: asset.source === "yahoo" ? "YAHOO" : asset.status,
  };
}

const AssetCatalogPanel: React.FC = () => {
  const [status, setStatus] = useState<BitgetCatalogStatus>("supported");
  const [lane, setLane] = useState<BitgetCatalogLane>("all");
  const [assetClass, setAssetClass] = useState<BitgetCatalogAssetClass>("all");
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState<BitgetCatalogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const catalogParams = useMemo(() => {
    const params = new URLSearchParams({
      status,
      lane,
      assetClass,
      limit: "8",
    });
    if (query.trim()) params.set("q", cleanAssetInput(query));
    return params;
  }, [assetClass, lane, query, status]);

  const csvHref = useMemo(() => {
    const params = new URLSearchParams(catalogParams);
    params.set("format", "csv");
    params.set("limit", "2000");
    return `/api/assets/bitget/catalog?${params.toString()}`;
  }, [catalogParams]);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const cleanedQuery = cleanAssetInput(query);
        if (cleanedQuery && status === "supported") {
          const params = new URLSearchParams({
            q: cleanedQuery,
            lane,
            assetClass,
            limit: "8",
          });
          const res = await fetch(`/api/assets/research/search?${params.toString()}`, { signal: controller.signal });
          const data = await res.json() as Partial<BitgetSearchResponse> & { message?: string };
          if (!res.ok) throw new Error(data.message || "Asset catalog unavailable");
          const rows = (data.results ?? []).map(catalogRowFromSuggestion);
          setCatalog({
            source: "research",
            providers: data.providers,
            createdAt: data.createdAt ?? new Date().toISOString(),
            status,
            supportedTotal: rows.length,
            excludedTotal: 0,
            discoveredTotal: rows.length,
            total: rows.length,
            count: rows.length,
            offset: 0,
            limit: 8,
            results: rows,
          });
        } else {
          const res = await fetch(`/api/assets/bitget/catalog?${catalogParams.toString()}`, { signal: controller.signal });
          const data = await res.json() as Partial<BitgetCatalogResponse> & { message?: string };
          if (!res.ok) throw new Error(data.message || "Asset catalog unavailable");
          setCatalog(data as BitgetCatalogResponse);
        }
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          setCatalog(null);
          setError(err?.message ?? "Asset catalog unavailable");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, query.trim() ? 220 : 0);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [catalogParams, query]);

  const updatedAt = catalog?.createdAt
    ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(catalog.createdAt))
    : "Pending";
  const providerSummary = catalog?.source === "research"
    ? (catalog.providers ?? []).map((provider) => providerName(provider)).join(" + ") || "Search"
    : "Bitget";

  return (
    <section className="analytics-catalog" aria-labelledby="asset-catalog-title">
      <div className="analytics-catalog-header">
        <div>
          <p className="home-kicker">ASSET CATALOG</p>
          <h2 id="asset-catalog-title">Supported analytics universe</h2>
        </div>
        <a className="analytics-action-btn analytics-catalog-download" href={csvHref}>
          <Download size={14} aria-hidden="true" />
          Bitget CSV
        </a>
      </div>

      <div className="analytics-catalog-controls">
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value as BitgetCatalogStatus)}>
            <option value="supported">Supported</option>
            <option value="excluded">Excluded</option>
            <option value="all">All</option>
          </select>
        </label>
        <label>
          <span>Lane</span>
          <select value={lane} onChange={(event) => setLane(event.target.value as BitgetCatalogLane)}>
            <option value="all">All lanes</option>
            <option value="digital-asset-trader">Digital Asset Trader</option>
            <option value="digital-assets">Digital Assets</option>
            <option value="tradfi">TradFi</option>
          </select>
        </label>
        <label>
          <span>Class</span>
          <select value={assetClass} onChange={(event) => setAssetClass(event.target.value as BitgetCatalogAssetClass)}>
            {catalogAssetClassOptions.map((option) => (
              <option key={option} value={option}>{option === "all" ? "All classes" : assetClassLabel(option)}</option>
            ))}
          </select>
        </label>
        <label className="analytics-catalog-search">
          <span>Search</span>
          <input
            value={query}
            onChange={(event) => setQuery(cleanAssetInput(event.target.value))}
            placeholder="BTC, NVDA, GOLD"
            maxLength={24}
          />
        </label>
      </div>

      <div className="analytics-catalog-metrics" aria-live="polite">
        <div><strong>{catalog?.supportedTotal ?? "..."}</strong><span>Supported</span></div>
        <div><strong>{catalog?.source === "research" ? providerSummary : catalog?.excludedTotal ?? "..."}</strong><span>{catalog?.source === "research" ? "Providers" : "Excluded"}</span></div>
        <div><strong>{catalog?.total ?? "..."}</strong><span>{catalogStatusLabel(status)} matches</span></div>
        <div><strong>{updatedAt}</strong><span>Catalog time</span></div>
      </div>

      {error && (
        <div className="analytics-catalog-state" role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {!error && (
        <div className="analytics-catalog-table-wrap">
          <table className="analytics-catalog-table">
            <caption>{catalogStatusLabel(status)} {catalogLaneLabel(lane)} assets</caption>
            <thead>
              <tr>
                <th scope="col">Provider</th>
                <th scope="col">Asset</th>
                <th scope="col">Symbol</th>
                <th scope="col">Product</th>
                <th scope="col">Class</th>
                <th scope="col">Lanes</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="analytics-catalog-state">
                    <Loader2 size={14} className="analytics-spin" aria-hidden="true" />
                    <span>Loading catalog</span>
                  </td>
                </tr>
              )}
              {!loading && (catalog?.results ?? []).map((row) => (
                <tr key={`${row.symbol}-${row.productType}-${row.supported ? "supported" : "excluded"}`}>
                  <td>{providerName(row.source)}</td>
                  <td>{row.baseCoin}</td>
                  <td>{row.symbol}</td>
                  <td>{row.productLabel}</td>
                  <td>{assetClassLabel(row.assetClass)}</td>
                  <td>{row.analyticsLanes.map(laneLabel).join(", ")}</td>
                  <td>
                    <span className={row.supported ? "analytics-catalog-ok" : "analytics-catalog-excluded"}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && catalog && catalog.results.length === 0 && (
                <tr>
                  <td colSpan={7} className="analytics-catalog-state">No matching assets</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const EmailReportForm: React.FC<{ content: string; asset: string }> = ({ content, asset }) => {
  const [open, setOpen]       = useState(false);
  const [recipient, setRecip] = useState(() => localStorage.getItem("tsm_email_receiver") ?? "");
  const [status, setStatus]   = useState<"idle" | "sending" | "ok" | "err">("idle");
  const [errMsg, setErrMsg]   = useState("");

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipient.trim()) return;
    setStatus("sending");
    try {
      const emailSender   = localStorage.getItem("tsm_email_sender")   ?? "";
      const emailPassword = localStorage.getItem("tsm_email_password") ?? "";
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (emailSender)   headers["x-email-sender"]   = emailSender;
      if (emailPassword) headers["x-email-password"] = emailPassword;

      const res = await fetch("/api/ttma/email", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          asset,
          content,
          timestamp: new Date().toISOString(),
          recipient: recipient.trim(),
        }),
      });
      const data = await res.json() as { status?: string; message?: string; detail?: string };
      if (res.ok && data.status === "success") {
        setStatus("ok");
      } else {
        setErrMsg(data.detail ?? data.message ?? `Server error ${res.status}`);
        setStatus("err");
      }
    } catch (err: any) {
      setErrMsg(err?.message ?? "Network error");
      setStatus("err");
    }
  };

  if (!open) {
    return (
      <button type="button" className="analytics-action-btn" onClick={() => setOpen(true)}>
        <Mail size={14} aria-hidden="true" />
        <span>Email report</span>
      </button>
    );
  }

  return (
    <form className="analytics-email-form" onSubmit={handleSend} aria-label="Email report">
      <input
        type="email"
        className="analytics-email-input"
        placeholder="recipient@example.com"
        value={recipient}
        onChange={(e) => setRecip(e.target.value)}
        autoFocus
        required
        aria-label="Recipient email"
        disabled={status === "sending" || status === "ok"}
      />
      {status === "idle" || status === "sending" ? (
        <>
          <button type="submit" className="analytics-action-btn" disabled={status === "sending"}>
            {status === "sending"
              ? <Loader2 size={14} className="analytics-spin" />
              : <Send size={14} />}
            <span>{status === "sending" ? "Sending…" : "Send"}</span>
          </button>
          <button type="button" className="analytics-action-btn" onClick={() => setOpen(false)}>Cancel</button>
        </>
      ) : status === "ok" ? (
        <span className="analytics-email-ok"><CheckCircle size={13} /> Sent to {recipient}</span>
      ) : (
        <>
          <span className="analytics-email-err"><XCircle size={13} /> {errMsg}</span>
          <button type="button" className="analytics-action-btn" onClick={() => setStatus("idle")}>Retry</button>
        </>
      )}
    </form>
  );
};

const PdfDownloadButton: React.FC<{
  content: string;
  asset: string;
  title: string;
  researchType: "digital-asset-trader" | "tradfi" | "digital-assets";
  provider?: string | null;
  model?: string | null;
  reportId: string | null;
}> = ({ content, asset, title, researchType, provider, model, reportId }) => {
  const [status, setStatus] = useState<"idle" | "working" | "error">("idle");

  const handleDownload = async () => {
    setStatus("working");
    try {
      await downloadReportPdf({
        reportId,
        title,
        symbol: asset,
        researchType,
        provider,
        model,
        markdown: content,
      });
      setStatus("idle");
    } catch {
      setStatus("error");
      window.setTimeout(() => setStatus("idle"), 3500);
    }
  };

  return (
    <button
      type="button"
      className="analytics-action-btn"
      onClick={handleDownload}
      disabled={status === "working"}
      aria-label="Save report as PDF"
      title={status === "error" ? "PDF export failed" : "Save report as PDF"}
    >
      {status === "working"
        ? <Loader2 size={14} className="analytics-spin" aria-hidden="true" />
        : <FileDown size={14} aria-hidden="true" />}
      <span>{status === "working" ? "PDF…" : status === "error" ? "PDF failed" : "Save PDF"}</span>
    </button>
  );
};

const MarkdownReport: React.FC<{
  content: string; asset: string; reportId: string | null;
  requestedProvider: TtmaProvider; resolvedProvider: TtmaProvider | null;
  requestedModel: string | null; resolvedModel: string | null;
}> = ({ content, asset, reportId, requestedProvider, resolvedProvider, requestedModel, resolvedModel }) => {
  const title = `${asset} Digital Asset Trader Research`;
  const handleDownload = () => downloadMarkdown(content, `tsm-${asset.toLowerCase()}-analysis.md`);

  return (
    <section className="analytics-result" aria-labelledby="analytics-result-title" aria-live="polite" aria-atomic="true">
      <div className="analytics-result-toolbar">
        <div className="analytics-result-meta">
          <CheckCircle size={16} aria-hidden="true" />
          <span id="analytics-result-title">{asset} Analysis Complete</span>
          <span className="analytics-inline-meta">Requested {requestedProvider.toUpperCase()}{requestedModel ? ` / ${requestedModel}` : ""}</span>
          {(resolvedProvider || resolvedModel) && <span className="analytics-inline-meta">Used {(resolvedProvider ?? requestedProvider).toUpperCase()}{resolvedModel ? ` / ${resolvedModel}` : ""}</span>}
        </div>
        <div className="analytics-result-actions">
          <button type="button" className="analytics-action-btn" onClick={handleDownload} aria-label="Download markdown report">
            <Download size={14} aria-hidden="true" />
            <span>Download .md</span>
          </button>
          <PdfDownloadButton
            content={content}
            asset={asset}
            title={title}
            researchType="digital-asset-trader"
            reportId={reportId}
          />
          <button type="button" className="analytics-action-btn" onClick={() => window.print()} aria-label="Print report">
            <Printer size={14} aria-hidden="true" />
            <span>Print</span>
          </button>
          <EmailReportForm content={content} asset={asset} />
          {reportId && (
            <Link href={`/reports?id=${encodeURIComponent(reportId)}`} className="analytics-action-btn">
              <span>Archive</span>
            </Link>
          )}
        </div>
      </div>
      <div className="analytics-result-body analytics-md">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </section>
  );
};

const GeminiReport: React.FC<{
  content: string;
  asset: string;
  provider: TradfiProvider;
  requestedProvider: TradfiProvider | null;
  requestedModel: string | null;
  model: string | null;
  grounded: boolean;
  sources: Array<{ uri: string; title: string }>;
  researchValidation: ResearchValidation | null;
  researchType: InstitutionalResearchType;
  reportId: string | null;
}> = ({ content, asset, provider, requestedProvider, requestedModel, model, grounded, sources, researchValidation, researchType, reportId }) => {
  const title = researchType === "digital-assets" ? "Digital Assets Research" : "TradFi Research";
  const evidenceStatus = researchValidation?.status;
  const noModelRun = !!researchValidation && researchValidation.segments.length === 0;
  const reportTitle = `${asset} ${researchType === "digital-assets" ? "Digital Assets Institutional Research" : "TradFi Institutional Research"}`;
  const handleDownload = () => downloadMarkdown(content, `tsm-${asset.toLowerCase()}-${researchType}-research.md`);

  return (
    <section className="analytics-result" aria-labelledby="gemini-result-title" aria-live="polite" aria-atomic="true">
      <div className="analytics-result-toolbar">
        <div className="analytics-result-meta">
          <Activity size={16} aria-hidden="true" />
          <span id="gemini-result-title">{asset} {title}{evidenceStatus ? ` · ${evidenceStatus.replaceAll("_", " ")}` : " · result returned"}</span>
        </div>
        <div className="analytics-result-provider-meta">
          {requestedProvider && <span className="analytics-inline-meta">Requested {requestedProvider.toUpperCase()}{requestedModel ? ` / ${requestedModel}` : ""}</span>}
          {noModelRun ? (
            <span className="analytics-inline-meta">No model run</span>
          ) : (
            <>
              <span className="analytics-inline-badge">{provider.toUpperCase()}</span>
              {model && <span className="analytics-inline-meta">{model}</span>}
            </>
          )}
          {grounded && <span className="analytics-inline-meta">Search metadata available</span>}
        </div>
        <div className="analytics-result-actions">
          <button type="button" className="analytics-action-btn" onClick={handleDownload} aria-label="Download analysis report">
            <Download size={14} aria-hidden="true" />
            <span>Download .md</span>
          </button>
          <PdfDownloadButton
            content={content}
            asset={asset}
            title={reportTitle}
            researchType={researchType}
            provider={provider}
            model={model}
            reportId={reportId}
          />
          <button type="button" className="analytics-action-btn" onClick={() => window.print()} aria-label="Print report">
            <Printer size={14} aria-hidden="true" />
            <span>Print</span>
          </button>
          <EmailReportForm content={content} asset={asset} />
          {reportId && (
            <Link href={`/reports?id=${encodeURIComponent(reportId)}`} className="analytics-action-btn">
              <span>Archive</span>
            </Link>
          )}
        </div>
      </div>
      <div className="analytics-result-body analytics-md">
        {researchValidation && <ResearchEvidenceDisplay validation={researchValidation} fallbackGrounded={grounded} />}
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        {sources.length > 0 && (
          <div className="analytics-source-block">
            <h3>Sources</h3>
            <ul>
              {sources.map((source) => (
                <li key={`${source.uri}-${source.title}`}>
                  <a href={source.uri} target="_blank" rel="noopener noreferrer">
                    {source.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Analytics: React.FC = () => {
  const [location, navigate] = useLocation();

  const initialSymbol = useMemo(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return getQuerySymbol(search);
  }, [location]);

  const [traderSymbol, setTraderSymbol] = useState(initialSymbol);
  const [tradfiSymbol, setTradfiSymbol] = useState(initialSymbol);
  const [digitalSymbol, setDigitalSymbol] = useState(initialSymbol);
  const hasGroqKey = hasVaultKey("groq");
  const hasGeminiKey = hasVaultKey("gemini");
  const hasOpenRouterKey = hasVaultKey("openrouter");

  const [state, setState] = useState<AnalysisState>({
    phase: "idle",
    asset: null,
    markdown: null,
    errorMsg: null,
    ttmaAvailable: null,
    provider: hasGroqKey ? "groq" : hasOpenRouterKey ? "openrouter" : "groq",
    resolvedProvider: null,
    requestedModel: null,
    resolvedModel: null,
    promptMode: hasGroqKey || hasOpenRouterKey || hasGeminiKey ? "short" : "guest",
    reportId: null,
    jobId: null,
    progress: [],
  });

  const [geminiState, setGeminiState] = useState<GeminiAnalysisState>({
    phase: "idle",
    asset: null,
    analysis: null,
    errorMsg: null,
    geminiAvailable: true,
    provider: hasGroqKey ? "groq" : hasOpenRouterKey ? "openrouter" : "groq",
    requestedProvider: null,
    requestedModel: null,
    model: null,
    grounded: false,
    sources: [],
    researchValidation: null,
    marketSnapshot: null,
    reportId: null,
    researchType: "tradfi",
    promptMode: hasGroqKey || hasOpenRouterKey || hasGeminiKey ? "short" : "guest",
  });

  const [digitalState, setDigitalState] = useState<GeminiAnalysisState>({
    phase: "idle",
    asset: null,
    analysis: null,
    errorMsg: null,
    geminiAvailable: true,
    provider: hasGroqKey ? "groq" : hasOpenRouterKey ? "openrouter" : "groq",
    requestedProvider: null,
    requestedModel: null,
    model: null,
    grounded: false,
    sources: [],
    researchValidation: null,
    marketSnapshot: null,
    reportId: null,
    researchType: "digital-assets",
    promptMode: hasGroqKey || hasOpenRouterKey || hasGeminiKey ? "short" : "guest",
  });

  useEffect(() => {
    checkTtmaStatus().then((available) =>
      setState((prev) => ({ ...prev, ttmaAvailable: available }))
    );
  }, []);

  // ── Digital-asset-trader async job streaming (ADR-002 A1.3) ──────────────
  // Submit returns a job_id immediately; we stream live progress over SSE,
  // persist the job_id in sessionStorage so a refresh/sleep reattaches, and
  // fall back to JSON polling if the stream can't be established.
  type TraderJobMeta = { asset: string; provider: TtmaProvider; promptMode: PromptMode };
  const TRADER_JOB_KEY = "tsm_ttma_active_job";
  const TRADER_JOB_DEADLINE_MS = 11 * 60 * 1000;

  const traderEsRef = useRef<AbortController | null>(null);
  const traderPollRef = useRef<AbortController | null>(null);
  const traderDeadlineRef = useRef(0);

  const stopTraderJobListeners = () => {
    if (traderEsRef.current) {
      traderEsRef.current.abort();
      traderEsRef.current = null;
    }
    if (traderPollRef.current) {
      traderPollRef.current.abort();
      traderPollRef.current = null;
    }
  };

  const clearTraderActiveJob = () => {
    try { sessionStorage.removeItem(TRADER_JOB_KEY); } catch { /* ignore */ }
  };

  const saveTraderActiveJob = (jobId: string, meta: TraderJobMeta) => {
    const ts = Date.now();
    traderDeadlineRef.current = ts + TRADER_JOB_DEADLINE_MS;
    try {
      sessionStorage.setItem(TRADER_JOB_KEY, JSON.stringify({ jobId, ...meta, ts }));
    } catch { /* ignore */ }
  };

  const finishTraderComplete = (
    markdown: string | null,
    reportId: string | null,
    provenance?: { requestedProvider?: TtmaProvider; resolvedProvider?: TtmaProvider; requestedModel?: string | null; resolvedModel?: string | null },
  ) => {
    stopTraderJobListeners();
    clearTraderActiveJob();
    setState((prev) => markdown
      ? {
          ...prev, phase: "complete", markdown, reportId, jobId: null,
          resolvedProvider: provenance?.resolvedProvider ?? prev.resolvedProvider,
          requestedModel: provenance?.requestedModel ?? prev.requestedModel,
          resolvedModel: provenance?.resolvedModel ?? prev.resolvedModel,
        }
      : {
          ...prev,
          phase: "error",
          jobId: null,
          errorMsg: "The analysis finished but returned no report content. Check the TTMA logs for the markdown generation error.",
        });
  };

  const finishTraderError = (message: string) => {
    stopTraderJobListeners();
    clearTraderActiveJob();
    setState((prev) => ({ ...prev, phase: "error", jobId: null, errorMsg: message }));
  };

  const pauseTraderRecovery = () => {
    stopTraderJobListeners();
    setState((prev) => ({
      ...prev,
      phase: "error",
      errorMsg: "Could not retrieve the analysis within 11 minutes. Your job is saved. Reload this page to reconnect; do not submit another analysis.",
    }));
  };

  // Each connection attempt is bounded; its saved job survives a deadline.
  const pollTraderJob = async (jobId: string) => {
    const controller = new AbortController();
    traderPollRef.current = controller;
    const headers = getClientIdentityHeaders();
    const remaining = traderDeadlineRef.current - Date.now();
    if (remaining <= 0) { pauseTraderRecovery(); return; }
    const deadlineTimer = setTimeout(() => {
      if (traderPollRef.current === controller) pauseTraderRecovery();
    }, remaining);
    try {
      while (!controller.signal.aborted && Date.now() < traderDeadlineRef.current) {
        try {
          const res = await fetch(`/api/ttma/jobs/${encodeURIComponent(jobId)}`, {
            headers, credentials: "include", signal: controller.signal,
          });
          const data = await res.json() as AnalyticsJobFailure & {
            status?: string; markdown?: string; reportId?: string | null; progress_messages?: string[];
            requestedProvider?: TtmaProvider; resolvedProvider?: TtmaProvider; requestedModel?: string | null; resolvedModel?: string | null;
            provider?: TtmaProvider; model?: string | null;
          };
          if (controller.signal.aborted) return;
          if (res.ok && data.status === "complete") {
            const md = typeof data.markdown === "string" && data.markdown.trim() ? data.markdown : null;
            finishTraderComplete(md, data.reportId ?? null, {
              requestedProvider: data.requestedProvider,
              resolvedProvider: data.resolvedProvider ?? data.provider,
              requestedModel: data.requestedModel,
              resolvedModel: data.resolvedModel ?? data.model,
            });
            return;
          }
          if (isRetryableAnalyticsJobFailure(data, res.status)) {
            // Keep the saved job while TTMA or archive finalization recovers.
          } else if (!res.ok || data.status === "error") {
            finishTraderError(data.detail ?? data.error ?? `Server returned ${res.status}`);
            return;
          } else if (Array.isArray(data.progress_messages)) {
            const lines = data.progress_messages;
            setState((prev) => ({ ...prev, progress: lines }));
          }
        } catch { /* transient transport failure — keep the saved job */ }
        if (controller.signal.aborted) return;
        await new Promise<void>((resolve) => {
          const done = () => {
            clearTimeout(timer);
            controller.signal.removeEventListener("abort", done);
            resolve();
          };
          const timer = setTimeout(done, 2500);
          controller.signal.addEventListener("abort", done, { once: true });
        });
      }
      if (!controller.signal.aborted) pauseTraderRecovery();
    } finally {
      clearTimeout(deadlineTimer);
      if (traderPollRef.current === controller) traderPollRef.current = null;
    }
  };

  const streamTraderJob = (jobId: string) => {
    stopTraderJobListeners();
    const controller = new AbortController();
    traderEsRef.current = controller;
    let settled = false;
    const fallback = () => {
      if (settled || controller.signal.aborted) return;
      settled = true;
      controller.abort();
      if (traderEsRef.current === controller) traderEsRef.current = null;
      void pollTraderJob(jobId);
    };
    void readAnalyticsJobStream(`/api/ttma/jobs/${encodeURIComponent(jobId)}/stream`, {
      headers: getClientIdentityHeaders(),
      signal: controller.signal,
      timeoutMs: Math.max(0, traderDeadlineRef.current - Date.now()),
      onEvent: (event, payload) => {
        if (controller.signal.aborted) return false;
        if (event === "progress") {
          try {
            const data = JSON.parse(payload) as { progress_messages?: string[] };
            if (Array.isArray(data.progress_messages)) {
              const lines = data.progress_messages;
              setState((prev) => ({ ...prev, progress: lines }));
            }
          } catch { /* ignore malformed progress frame */ }
        } else if (event === "complete") {
          settled = true;
          try {
            const data = JSON.parse(payload) as {
              markdown?: string; reportId?: string | null; requestedProvider?: TtmaProvider; resolvedProvider?: TtmaProvider;
              requestedModel?: string | null; resolvedModel?: string | null; provider?: TtmaProvider; model?: string | null;
            };
            const md = typeof data.markdown === "string" && data.markdown.trim() ? data.markdown : null;
            finishTraderComplete(md, data.reportId ?? null, {
              requestedProvider: data.requestedProvider,
              resolvedProvider: data.resolvedProvider ?? data.provider,
              requestedModel: data.requestedModel,
              resolvedModel: data.resolvedModel ?? data.model,
            });
          } catch {
            finishTraderError("Received a malformed completion event from the analytics service.");
          }
          return false;
        } else if (event === "failed") {
          try {
            const data = JSON.parse(payload) as AnalyticsJobFailure;
            if (isRetryableAnalyticsJobFailure(data)) { fallback(); return false; }
            settled = true;
            finishTraderError(data.detail ?? data.error ?? "Analysis failed");
          } catch {
            settled = true;
            finishTraderError("Analysis failed");
          }
          return false;
        }
      },
    }).then(fallback).catch((error: unknown) => {
      if (settled || controller.signal.aborted) return;
      if (error instanceof AnalyticsStreamHttpError && !isRetryableAnalyticsJobFailure(error.failure, error.status)) {
        settled = true;
        finishTraderError(error.message);
      } else {
        fallback();
      }
    });
  };

  // Reattach to an in-flight job after a refresh / sleep / navigation.
  useEffect(() => {
    let raw: string | null = null;
    try { raw = sessionStorage.getItem(TRADER_JOB_KEY); } catch { raw = null; }
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as { jobId?: string; asset?: string; provider?: TtmaProvider; promptMode?: PromptMode; ts?: number };
      if (!saved.jobId) {
        clearTraderActiveJob();
        return;
      }
      traderDeadlineRef.current = Date.now() + TRADER_JOB_DEADLINE_MS;
      setState((prev) => ({
        ...prev,
        phase: "analyzing",
        asset: saved.asset ?? prev.asset,
        markdown: null,
        errorMsg: null,
        reportId: null,
        jobId: saved.jobId!,
        progress: [],
        provider: saved.provider ?? prev.provider,
        promptMode: saved.promptMode ?? prev.promptMode,
      }));
      streamTraderJob(saved.jobId);
    } catch {
      clearTraderActiveJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tear down any open stream/poll on unmount.
  useEffect(() => () => stopTraderJobListeners(), []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let nextSymbol = cleanAssetInput(traderSymbol) || "BTC";
    try {
      const supportedAsset = await resolveSupportedAssetForLane(nextSymbol, "digital-asset-trader");
      nextSymbol = supportedAsset.analysisSymbol ?? supportedAsset.baseCoin;
      setTraderSymbol(nextSymbol);
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        phase: "error",
        asset: nextSymbol,
        markdown: null,
        errorMsg: err?.message ?? unsupportedAssetMessage(nextSymbol, "digital-asset-trader"),
        reportId: null,
      }));
      return;
    }
    navigate(`/analytics?symbol=${encodeURIComponent(nextSymbol)}`);

    stopTraderJobListeners();
    setState((prev) => ({
      phase: "analyzing",
      asset: nextSymbol,
      markdown: null,
      errorMsg: null,
      ttmaAvailable: prev.ttmaAvailable,
      provider: prev.provider,
      resolvedProvider: null,
      requestedModel: null,
      resolvedModel: null,
      promptMode: prev.promptMode,
      reportId: null,
      jobId: null,
      progress: [],
    }));

    // Forward the user's vault keys so BYOK actually reaches TTMA.
    const headers: Record<string, string> = { "Content-Type": "application/json", ...getClientIdentityHeaders() };
    if (state.promptMode !== "guest") {
      const groqKey = getVaultKey("groq");
      const geminiKey = getVaultKey("gemini");
      const openRouterKey = getVaultKey("openrouter");
      if (groqKey) headers["x-groq-key"] = groqKey;
      if (geminiKey) headers["x-gemini-key"] = geminiKey;
      if (openRouterKey) headers["x-openrouter-key"] = openRouterKey;
    }
    const alphaVantageKey = getVaultKey("alphaVantage");
    const polygonKey = getVaultKey("polygon");
    const finnhubKey = getVaultKey("finnhub");
    const nytKey = getVaultKey("nyt");
    const cmcKey = getVaultKey("cmc");
    if (alphaVantageKey) headers["x-alpha-vantage-key"] = alphaVantageKey;
    if (polygonKey) headers["x-polygon-key"] = polygonKey;
    if (finnhubKey) headers["x-finnhub-key"] = finnhubKey;
    if (nytKey) headers["x-nyt-key"] = nytKey;
    if (cmcKey) headers["x-cmc-key"] = cmcKey;

    try {
      // A1.3: non-blocking submit. The endpoint returns a job_id immediately;
      // we then stream live progress (with a polling fallback) and persist the
      // job so a refresh/sleep can reattach.
      const res = await fetch("/api/ttma/analyze", {
        method: "POST",
        headers,
        body: JSON.stringify({ symbol: nextSymbol, provider: state.provider, promptMode: state.promptMode }),
        credentials: "include",
      });
      const data = await res.json() as {
        job_id?: string; status?: string; markdown?: string; reportId?: string | null;
        requestedProvider?: TtmaProvider; resolvedProvider?: TtmaProvider; requestedModel?: string | null; resolvedModel?: string | null;
        provider?: TtmaProvider; model?: string | null;
        error?: string; detail?: string;
      };

      if (!res.ok) {
        finishTraderError(data.detail ?? data.error ?? `Server returned ${res.status}`);
        return;
      }

      // A guest cache hit (or any already-complete path) returns the full
      // markdown payload directly instead of a queued job — handle both shapes.
      if (data.status === "complete" || typeof data.markdown === "string") {
        const md = typeof data.markdown === "string" && data.markdown.trim() ? data.markdown : null;
        finishTraderComplete(md, data.reportId ?? null, {
          requestedProvider: data.requestedProvider,
          resolvedProvider: data.resolvedProvider ?? data.provider,
          requestedModel: data.requestedModel,
          resolvedModel: data.resolvedModel ?? data.model,
        });
        return;
      }

      if (!data.job_id) {
        finishTraderError("The analytics service did not return a job id.");
        return;
      }

      const meta: TraderJobMeta = { asset: nextSymbol, provider: state.provider, promptMode: state.promptMode };
      const jobId = data.job_id;
      setState((prev) => ({
        ...prev,
        jobId,
        requestedModel: data.requestedModel ?? null,
      }));
      saveTraderActiveJob(jobId, meta);
      streamTraderJob(jobId);
    } catch (err: any) {
      finishTraderError(err?.message ?? "Unexpected error");
    }
  };

  const handleReset = () => {
    stopTraderJobListeners();
    clearTraderActiveJob();
    setState((prev) => ({
      phase: "idle",
      asset: null,
      markdown: null,
      errorMsg: null,
      ttmaAvailable: prev.ttmaAvailable,
      provider: prev.provider,
      promptMode: prev.promptMode,
      reportId: null,
      jobId: null,
      progress: [],
      resolvedProvider: null,
      requestedModel: null,
      resolvedModel: null,
    }));
  };

  const handleInstitutionalSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    researchType: InstitutionalResearchType,
  ) => {
    event.preventDefault();
    const setTargetState = researchType === "digital-assets" ? setDigitalState : setGeminiState;
    const currentState = researchType === "digital-assets" ? digitalState : geminiState;
    const currentSymbol = researchType === "digital-assets" ? digitalSymbol : tradfiSymbol;
    const setCurrentSymbol = researchType === "digital-assets" ? setDigitalSymbol : setTradfiSymbol;
    const lane: AnalyticsLane = researchType === "digital-assets" ? "digital-assets" : "tradfi";
    let nextSymbol = cleanAssetInput(currentSymbol) || "BTC";
    try {
      const supportedAsset = await resolveSupportedAssetForLane(nextSymbol, lane);
      nextSymbol = supportedAsset.analysisSymbol ?? supportedAsset.baseCoin;
      setCurrentSymbol(nextSymbol);
    } catch (err: any) {
      setTargetState((prev) => ({
        ...prev,
        phase: "error",
        asset: nextSymbol,
        analysis: null,
        errorMsg: err?.message ?? unsupportedAssetMessage(nextSymbol, lane),
        model: null,
        grounded: false,
        sources: [],
        researchValidation: null,
        marketSnapshot: null,
        reportId: null,
        researchType,
      }));
      return;
    }
    navigate(`/analytics?symbol=${encodeURIComponent(nextSymbol)}`);

    setTargetState((prev) => ({
      phase: "analyzing",
      asset: nextSymbol,
      analysis: null,
      errorMsg: null,
      geminiAvailable: prev.geminiAvailable,
      provider: prev.provider,
      requestedProvider: prev.provider,
      requestedModel: null,
      model: null,
      grounded: false,
      sources: [],
      researchValidation: null,
      marketSnapshot: null,
      reportId: null,
      researchType,
      promptMode: prev.promptMode,
    }));

    try {
      const headers: Record<string, string> = { "Content-Type": "application/json", ...getClientIdentityHeaders() };
      const geminiKey = currentState.promptMode === "guest" ? null : getVaultKey("gemini");
      const groqKey = currentState.promptMode === "guest" ? null : getVaultKey("groq");
      const openRouterKey = currentState.promptMode === "guest" ? null : getVaultKey("openrouter");
      const alphaVantageKey = getVaultKey("alphaVantage");
      const polygonKey = getVaultKey("polygon");
      const finnhubKey = getVaultKey("finnhub");
      const cmcKey = getVaultKey("cmc");
      const fredKey = getVaultKey("fred");
      if (geminiKey) headers["x-gemini-key"] = geminiKey;
      if (groqKey) headers["x-groq-key"] = groqKey;
      if (openRouterKey) headers["x-openrouter-key"] = openRouterKey;
      if (alphaVantageKey) headers["x-alpha-vantage-key"] = alphaVantageKey;
      if (polygonKey) headers["x-polygon-key"] = polygonKey;
      if (finnhubKey) headers["x-finnhub-key"] = finnhubKey;
      if (cmcKey) headers["x-cmc-key"] = cmcKey;
      if (fredKey) headers["x-fred-key"] = fredKey;

      const res = await fetch("/api/tradfi-analysis", {
        method: "POST",
        headers,
        body: JSON.stringify({
          symbol: nextSymbol,
          provider: currentState.provider,
          researchType,
          promptMode: currentState.promptMode,
        }),
        credentials: "include",
      });
      const data = await res.json() as {
        status?: ResearchValidation["status"] | "error";
        analysis?: string;
        provider?: TradfiProvider;
        requestedProvider?: TradfiProvider;
        resolvedProvider?: TradfiProvider;
        requestedModel?: string | null;
        resolvedModel?: string | null;
        model?: string;
        grounded?: boolean;
        sources?: Array<{ uri: string; title: string }>;
        researchValidation?: ResearchValidation | null;
        marketSnapshot?: unknown;
        reportId?: string | null;
        researchType?: InstitutionalResearchType;
        error?: string;
        detail?: string;
      };

      if (!res.ok || !["complete", "partial", "insufficient_evidence"].includes(data.status ?? "")) {
        setTargetState((prev) => ({
          ...prev,
          phase: "error",
          errorMsg: data.detail ?? data.error ?? `Server returned ${res.status}`,
        }));
        return;
      }

      setTargetState((prev) => ({
        ...prev,
        phase: "complete",
        analysis: data.analysis ?? null,
        provider: data.resolvedProvider ?? data.provider ?? prev.provider,
        requestedProvider: data.requestedProvider ?? currentState.provider,
        requestedModel: data.requestedModel ?? null,
        model: data.resolvedModel ?? data.model ?? null,
        grounded: data.grounded === true,
        sources: data.sources ?? [],
        researchValidation: data.researchValidation ?? null,
        marketSnapshot: data.marketSnapshot ?? null,
        reportId: data.reportId ?? null,
        researchType: data.researchType ?? researchType,
        promptMode: prev.promptMode,
      }));
    } catch (err: any) {
      setTargetState((prev) => ({ ...prev, phase: "error", errorMsg: err?.message ?? "Unexpected error" }));
    }
  };

  const handleGeminiSubmit = (event: React.FormEvent<HTMLFormElement>) =>
    handleInstitutionalSubmit(event, "tradfi");

  const handleDigitalSubmit = (event: React.FormEvent<HTMLFormElement>) =>
    handleInstitutionalSubmit(event, "digital-assets");

  const handleGeminiReset = () => {
    setGeminiState((prev) => ({
      phase: "idle",
      asset: null,
      analysis: null,
      errorMsg: null,
      geminiAvailable: prev.geminiAvailable,
      provider: prev.provider,
      requestedProvider: null,
      requestedModel: null,
      model: null,
      grounded: false,
      sources: [],
      researchValidation: null,
      marketSnapshot: null,
      reportId: null,
      researchType: "tradfi",
      promptMode: prev.promptMode,
    }));
  };

  const handleDigitalReset = () => {
    setDigitalState((prev) => ({
      phase: "idle",
      asset: null,
      analysis: null,
      errorMsg: null,
      geminiAvailable: prev.geminiAvailable,
      provider: prev.provider,
      requestedProvider: null,
      requestedModel: null,
      model: null,
      grounded: false,
      sources: [],
      researchValidation: null,
      marketSnapshot: null,
      reportId: null,
      researchType: "digital-assets",
      promptMode: prev.promptMode,
    }));
  };

  const isRunning = state.phase === "analyzing";
  const isGeminiRunning = geminiState.phase === "analyzing";
  const isDigitalRunning = digitalState.phase === "analyzing";
  const isAnyResearchRunning = isRunning || isGeminiRunning || isDigitalRunning;

  return (
    <>
      <Helmet>
        <title>Research — Trader, TradFi & Digital Assets | Tylee Smart Markets</title>
        <meta name="description" content="Grade-A market research for regular investors: short-term digital asset trader research, institutional TradFi reports, and deep digital assets research powered by Gemini, Groq, or OpenRouter." />
        <link rel="canonical" href="https://tyl.ee/analytics" />
        <meta property="og:title" content="Research — Trader, TradFi & Digital Assets | Tylee Smart Markets" />
        <meta property="og:description" content="Three research lanes: real-time digital asset trader research, institutional TradFi, and deep digital assets reports with archive, print, download, and email actions." />
        <meta property="og:url" content="https://tyl.ee/analytics" />
        <meta name="twitter:title" content="Research — Trader, TradFi & Digital Assets | Tylee Smart Markets" />
        <meta name="twitter:description" content="Short-term digital asset trading research, institutional TradFi, and deep digital assets reports via Gemini, Groq, or OpenRouter." />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <a href="#analytics-main" className="skip-link">Skip to main content</a>
        <AppHeader brandText="Analytics" rightSlot={<KeyStatusBadge hasKey={hasGroqKey || hasGeminiKey || hasOpenRouterKey} />} />

      <main id="analytics-main" className="analytics-main-v2">

        <ResearchDossierEntry />
        <ExposureReactionDossierEntry />
        <EventEvidenceReviewEntry />
        {/* Research command panels */}
        <section className="analytics-dual-panels" aria-label="Analysis tools">
          <div className="analytics-panel-section">
            <div className="analytics-command">
              <div>
                <p className="home-kicker">
                  DIGITAL ASSET TRADER
                  <ProviderBadge provider={state.provider} />
                  <span className="analytics-gemini-badge analytics-mode-badge">{promptModeLabel(state.promptMode)}</span>
                </p>
                <h2 id="ttma-title">Digital Asset Trader Research</h2>
                <p className="home-summary">
                  Short-term trade research for crypto and digital assets using real-time market data,
                  technicals, sentiment, derivatives, macro, DeFi, liquidations, order book, and price levels for intraday and swing setups.
                </p>
              </div>

              <form className="home-symbol-form analytics-form" onSubmit={handleSubmit} aria-label="Run digital asset trader research">
                <p className="analytics-key-prompt">A separate 30-day outcome pilot may include newly accepted Trader jobs when active. Reports describe sampled movement only; they do not show accuracy or profitability.</p>
                <label htmlFor="analytics-symbol-ttma">Ticker</label>
                <label htmlFor="analytics-provider-ttma">Provider</label>
                <div className="analytics-provider-row">
                  <select
                    id="analytics-provider-ttma"
                    className="analytics-provider-select"
                    value={state.provider}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        provider: e.target.value as TtmaProvider,
                      }))
                    }
                    disabled={isAnyResearchRunning}
                  >
                    {ACTIVE_ANALYSIS_PROVIDERS.map((provider) => (
                      <option key={provider} value={provider}>{providerOptionLabel(provider)}</option>
                    ))}
                  </select>
                </div>
                <label htmlFor="analytics-mode-ttma">Prompt</label>
                <div className="analytics-provider-row">
                  <PromptModeSelect
                    id="analytics-mode-ttma"
                    value={state.promptMode}
                    onChange={(promptMode) =>
                      setState((prev) => ({
                        ...prev,
                        promptMode,
                      }))
                    }
                    disabled={isAnyResearchRunning}
                  />
                </div>
                <div className="home-symbol-row">
                  <AssetSearchInput
                    id="analytics-symbol-ttma"
                    value={traderSymbol}
                    lane="digital-asset-trader"
                    onChange={setTraderSymbol}
                    onSelect={(asset) => setTraderSymbol(asset.analysisSymbol ?? asset.baseCoin)}
                    disabled={isAnyResearchRunning}
                  />
                  <button type="submit" disabled={isRunning || state.ttmaAvailable === false || isGeminiRunning || isDigitalRunning}>
                    {isRunning
                      ? <Loader2 size={16} className="analytics-spin" aria-hidden="true" />
                      : <Activity size={16} aria-hidden="true" />}
                    <span>{isRunning ? "Analyzing…" : "Analyze"}</span>
                  </button>
                </div>

                {state.promptMode === "guest" && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    Guest compact uses shared capacity when available and does not send your saved LLM keys.
                  </p>
                )}
                {state.promptMode !== "guest" && state.provider === "groq" && !hasGroqKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No Groq key saved.{" "}
                    <Link href="/get-started">Add yours on the Keys page</Link>
                    {" "}to use your own API quota, or use limited guest capacity when available.
                  </p>
                )}
                {state.provider === "gemini" && (
                  <p className="analytics-key-prompt analytics-key-prompt--warn">Gemini Search grounding is unavailable under the free-only policy. Select Groq or OpenRouter.</p>
                )}
                {state.promptMode !== "guest" && state.provider === "openrouter" && !hasOpenRouterKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No OpenRouter key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run trader research with OpenRouter.
                  </p>
                )}
                {state.ttmaAvailable === false && (
                  <p className="analytics-key-prompt analytics-key-prompt--warn">
                    <AlertTriangle size={12} aria-hidden="true" />
                    Analytics service is starting up — please wait a moment and try again.
                  </p>
                )}
              </form>
            </div>
          </div>

          <div className="analytics-panel-section">
            <div className="analytics-command">
              <div>
                <p className="home-kicker">
                  TRADFI RESEARCH
                  <span className="analytics-gemini-badge">{geminiState.provider.toUpperCase()}</span>
                  <span className="analytics-gemini-badge analytics-mode-badge">{promptModeLabel(geminiState.promptMode)}</span>
                </p>
                <h2 id="gemini-title">Institutional market research</h2>
                <p className="home-summary">
                  Deep-dive institutional research for equities, indexes, commodities, rates, and macro themes.
                  Uses Yahoo-friendly symbol lookup, provider-dated market-data snapshots, web grounding where available, and plain-English conclusions.
                </p>
              </div>

              <form className="home-symbol-form analytics-form" onSubmit={handleGeminiSubmit} aria-label="Run TradFi analysis">
                <label htmlFor="analytics-symbol-gemini">Ticker</label>
                <label htmlFor="analytics-provider-gemini">Provider</label>
                <div className="analytics-provider-row">
                  <select
                    id="analytics-provider-gemini"
                    className="analytics-provider-select"
                    value={geminiState.provider}
                    onChange={(e) =>
                      setGeminiState((prev) => ({
                        ...prev,
                        provider: e.target.value as TradfiProvider,
                      }))
                    }
                    disabled={isAnyResearchRunning}
                  >
                    {ACTIVE_ANALYSIS_PROVIDERS.map((provider) => (
                      <option key={provider} value={provider}>{providerOptionLabel(provider)}</option>
                    ))}
                  </select>
                </div>
                <label htmlFor="analytics-mode-gemini">Prompt</label>
                <div className="analytics-provider-row">
                  <PromptModeSelect
                    id="analytics-mode-gemini"
                    value={geminiState.promptMode}
                    onChange={(promptMode) =>
                      setGeminiState((prev) => ({
                        ...prev,
                        promptMode,
                      }))
                    }
                    disabled={isAnyResearchRunning}
                  />
                </div>
                <div className="home-symbol-row">
                  <AssetSearchInput
                    id="analytics-symbol-gemini"
                    value={tradfiSymbol}
                    lane="tradfi"
                    onChange={setTradfiSymbol}
                    onSelect={(asset) => setTradfiSymbol(asset.analysisSymbol ?? asset.baseCoin)}
                    disabled={isAnyResearchRunning}
                  />
                  <button type="submit" disabled={isGeminiRunning || isRunning || isDigitalRunning}>
                    {isGeminiRunning
                      ? <Loader2 size={16} className="analytics-spin" aria-hidden="true" />
                      : <BrainCircuit size={16} aria-hidden="true" />}
                    <span>{isGeminiRunning ? "Analyzing…" : "Analyze"}</span>
                  </button>
                </div>
                {geminiState.promptMode === "guest" && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    Guest compact uses a smaller institutional prompt with shared capacity when available.
                  </p>
                )}
                {geminiState.provider === "gemini" && (
                  <p className="analytics-key-prompt analytics-key-prompt--warn">Gemini Search grounding is unavailable under the free-only policy. This selection will return an entitlement status without starting a request.</p>
                )}
                {geminiState.promptMode !== "guest" && geminiState.provider === "gemini" && !hasGeminiKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No Gemini key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run grounded TradFi analysis.
                  </p>
                )}
                {geminiState.promptMode !== "guest" && geminiState.provider === "groq" && !hasGroqKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No Groq key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run Groq-backed TradFi analysis.
                  </p>
                )}
                {geminiState.promptMode !== "guest" && geminiState.provider === "openrouter" && !hasOpenRouterKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No OpenRouter key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run OpenRouter-backed TradFi analysis.
                  </p>
                )}
              </form>
            </div>
          </div>

          <div className="analytics-panel-section">
            <div className="analytics-command">
              <div>
                <p className="home-kicker">
                  DIGITAL ASSETS RESEARCH
                  <span className="analytics-gemini-badge">{digitalState.provider.toUpperCase()}</span>
                  <span className="analytics-gemini-badge analytics-mode-badge">{promptModeLabel(digitalState.promptMode)}</span>
                </p>
                <h2 id="digital-assets-title">Institutional digital assets research</h2>
                <p className="home-summary">
                  Deep research for BTC, ETH, tokens, stablecoins, DeFi, and exchange-linked assets.
                  Built for Yahoo crypto pairs, Bitget-listed assets, tokenomics, liquidity, regulation, custody, risk, and 12-18 month outlooks.
                </p>
              </div>

              <form className="home-symbol-form analytics-form" onSubmit={handleDigitalSubmit} aria-label="Run digital assets research">
                <label htmlFor="analytics-symbol-digital">Ticker</label>
                <label htmlFor="analytics-provider-digital">Provider</label>
                <div className="analytics-provider-row">
                  <select
                    id="analytics-provider-digital"
                    className="analytics-provider-select"
                    value={digitalState.provider}
                    onChange={(e) =>
                      setDigitalState((prev) => ({
                        ...prev,
                        provider: e.target.value as TradfiProvider,
                      }))
                    }
                    disabled={isAnyResearchRunning}
                  >
                    {ACTIVE_ANALYSIS_PROVIDERS.map((provider) => (
                      <option key={provider} value={provider}>{providerOptionLabel(provider)}</option>
                    ))}
                  </select>
                </div>
                <label htmlFor="analytics-mode-digital">Prompt</label>
                <div className="analytics-provider-row">
                  <PromptModeSelect
                    id="analytics-mode-digital"
                    value={digitalState.promptMode}
                    onChange={(promptMode) =>
                      setDigitalState((prev) => ({
                        ...prev,
                        promptMode,
                      }))
                    }
                    disabled={isAnyResearchRunning}
                  />
                </div>
                <div className="home-symbol-row">
                  <AssetSearchInput
                    id="analytics-symbol-digital"
                    value={digitalSymbol}
                    lane="digital-assets"
                    onChange={setDigitalSymbol}
                    onSelect={(asset) => setDigitalSymbol(asset.analysisSymbol ?? asset.baseCoin)}
                    disabled={isAnyResearchRunning}
                  />
                  <button type="submit" disabled={isDigitalRunning || isRunning || isGeminiRunning}>
                    {isDigitalRunning
                      ? <Loader2 size={16} className="analytics-spin" aria-hidden="true" />
                      : <BrainCircuit size={16} aria-hidden="true" />}
                    <span>{isDigitalRunning ? "Researching…" : "Research"}</span>
                  </button>
                </div>
                {digitalState.promptMode === "guest" && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    Guest compact uses a smaller digital asset prompt with shared capacity when available.
                  </p>
                )}
                {digitalState.provider === "gemini" && (
                  <p className="analytics-key-prompt analytics-key-prompt--warn">Gemini Search grounding is unavailable under the free-only policy. This selection will return an entitlement status without starting a request.</p>
                )}
                {digitalState.promptMode !== "guest" && digitalState.provider === "gemini" && !hasGeminiKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No Gemini key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run grounded digital assets research.
                  </p>
                )}
                {digitalState.promptMode !== "guest" && digitalState.provider === "groq" && !hasGroqKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No Groq key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run Groq-backed digital assets research.
                  </p>
                )}
                {digitalState.promptMode !== "guest" && digitalState.provider === "openrouter" && !hasOpenRouterKey && (
                  <p className="analytics-key-prompt">
                    <KeyRound size={12} aria-hidden="true" />
                    No OpenRouter key saved. <Link href="/get-started">Add it in Keys &amp; Help</Link> to run OpenRouter-backed digital assets research.
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="analytics-access-guide" aria-label="Prompt mode access guide">
          <div>
            <strong>Guest compact</strong>
            <span>No key required. Smaller prompt, shared capacity, tight limits.</span>
          </div>
          <div>
            <strong>BYOK short form</strong>
            <span>Default with saved keys. Uses your provider quota with a lighter prompt.</span>
          </div>
          <div>
            <strong>BYOK long form</strong>
            <span>Deepest report. Uses your provider quota and can take longer.</span>
          </div>
        </section>

        <AssetCatalogPanel />

        {/* Running states */}
        {isRunning && (
          <section className="analytics-progress" aria-live="polite" aria-label="Digital asset trader research in progress">
            <div className="analytics-progress-header">
              <Loader2 size={18} className="analytics-spin" aria-hidden="true" />
              <span>Running {state.asset} digital asset trader research…</span>
            </div>
            <p className="analytics-progress-note">
              <Clock size={12} aria-hidden="true" />
              {promptModeSummary(state.promptMode)} Provider: {state.provider.toUpperCase()}.
            </p>
            {state.progress.length > 0 && (
              <ul className="analytics-progress-feed" aria-label="Live analysis progress">
                {state.progress.slice(-4).map((line, i) => (
                  <li key={`${i}-${line}`}>{line.replace(/^\[[^\]]*\]\s*/, "")}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {isGeminiRunning && (
          <section className="analytics-progress" aria-live="polite" aria-label="TradFi analysis in progress">
            <div className="analytics-progress-header">
              <Loader2 size={18} className="analytics-spin" aria-hidden="true" />
              <span>Running {geminiState.asset} TradFi research…</span>
            </div>
            <p className="analytics-progress-note">
              <Clock size={12} aria-hidden="true" />
              {promptModeSummary(geminiState.promptMode)} Provider: {geminiState.provider.toUpperCase()}.
            </p>
          </section>
        )}

        {isDigitalRunning && (
          <section className="analytics-progress" aria-live="polite" aria-label="Digital assets research in progress">
            <div className="analytics-progress-header">
              <Loader2 size={18} className="analytics-spin" aria-hidden="true" />
              <span>Running {digitalState.asset} digital assets research…</span>
            </div>
            <p className="analytics-progress-note">
              <Clock size={12} aria-hidden="true" />
              {promptModeSummary(digitalState.promptMode)} Provider: {digitalState.provider.toUpperCase()}.
            </p>
          </section>
        )}

        {/* Error states */}
        {state.phase === "error" && (
          <section className="analytics-error" aria-live="polite" role="alert">
            <div className="analytics-error-header">
              <XCircle size={18} aria-hidden="true" />
              <span>Digital asset trader research failed</span>
              <button type="button" className="analytics-action-btn" onClick={handleReset}>
                <RotateCcw size={14} aria-hidden="true" /> Try again
              </button>
            </div>
            <p className="analytics-error-detail">{state.errorMsg}</p>
          </section>
        )}

        {geminiState.phase === "error" && (
          <section className="analytics-error" aria-live="polite" role="alert">
            <div className="analytics-error-header">
              <XCircle size={18} aria-hidden="true" />
              <span>TradFi analysis failed</span>
              <button type="button" className="analytics-action-btn" onClick={handleGeminiReset}>
                <RotateCcw size={14} aria-hidden="true" /> Try again
              </button>
            </div>
            <p className="analytics-error-detail">{geminiState.errorMsg}</p>
          </section>
        )}

        {digitalState.phase === "error" && (
          <section className="analytics-error" aria-live="polite" role="alert">
            <div className="analytics-error-header">
              <XCircle size={18} aria-hidden="true" />
              <span>Digital assets research failed</span>
              <button type="button" className="analytics-action-btn" onClick={handleDigitalReset}>
                <RotateCcw size={14} aria-hidden="true" /> Try again
              </button>
            </div>
            <p className="analytics-error-detail">{digitalState.errorMsg}</p>
          </section>
        )}

        {/* Results */}
        {(state.phase === "complete" || geminiState.phase === "complete" || digitalState.phase === "complete") && (
          <section className="analytics-results-grid">
            {state.phase === "complete" && (
              <div className="analytics-result-column">
                {state.markdown ? (
                  <MarkdownReport
                    content={state.markdown}
                    asset={state.asset ?? traderSymbol}
                    reportId={state.reportId}
                    requestedProvider={state.provider}
                    resolvedProvider={state.resolvedProvider}
                    requestedModel={state.requestedModel}
                    resolvedModel={state.resolvedModel}
                  />
                ) : (
                  <section className="analytics-error" aria-live="polite" role="alert">
                    <div className="analytics-error-header">
                      <CheckCircle size={18} aria-hidden="true" />
                      <span>{state.asset} analysis completed — no report content returned</span>
                    </div>
                    <p className="analytics-error-detail">
                      The analysis engine finished but returned an empty report. This usually means the API keys are missing or the analysis engine encountered an error internally. Check the TTMA logs.
                    </p>
                  </section>
                )}
                <div className="analytics-result-footer">
                  <button type="button" className="analytics-action-btn" onClick={handleReset}>
                    <RotateCcw size={14} aria-hidden="true" /> New trader research
                  </button>
                </div>
              </div>
            )}

            {geminiState.phase === "complete" && (
              <div className="analytics-result-column">
                {geminiState.analysis ? (
                  <GeminiReport
                    content={geminiState.analysis}
                    asset={geminiState.asset ?? tradfiSymbol}
                    provider={geminiState.provider}
                    requestedProvider={geminiState.requestedProvider}
                    requestedModel={geminiState.requestedModel}
                    model={geminiState.model}
                    grounded={geminiState.grounded}
                    sources={geminiState.sources}
                    researchValidation={geminiState.researchValidation}
                    researchType={geminiState.researchType}
                    reportId={geminiState.reportId}
                  />
                ) : (
                  <section className="analytics-error" aria-live="polite" role="alert">
                    <div className="analytics-error-header">
                      <CheckCircle size={18} aria-hidden="true" />
                      <span>{geminiState.asset} analysis completed — no report content returned</span>
                    </div>
                  </section>
                )}
                <div className="analytics-result-footer">
                  <button type="button" className="analytics-action-btn" onClick={handleGeminiReset}>
                    <RotateCcw size={14} aria-hidden="true" /> New TradFi research
                  </button>
                </div>
              </div>
            )}

            {digitalState.phase === "complete" && (
              <div className="analytics-result-column">
                {digitalState.analysis ? (
                  <GeminiReport
                    content={digitalState.analysis}
                    asset={digitalState.asset ?? digitalSymbol}
                    provider={digitalState.provider}
                    requestedProvider={digitalState.requestedProvider}
                    requestedModel={digitalState.requestedModel}
                    model={digitalState.model}
                    grounded={digitalState.grounded}
                    sources={digitalState.sources}
                    researchValidation={digitalState.researchValidation}
                    researchType={digitalState.researchType}
                    reportId={digitalState.reportId}
                  />
                ) : (
                  <section className="analytics-error" aria-live="polite" role="alert">
                    <div className="analytics-error-header">
                      <CheckCircle size={18} aria-hidden="true" />
                      <span>{digitalState.asset} research completed — no report content returned</span>
                    </div>
                  </section>
                )}
                <div className="analytics-result-footer">
                  <button type="button" className="analytics-action-btn" onClick={handleDigitalReset}>
                    <RotateCcw size={14} aria-hidden="true" /> New digital assets research
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Idle panels */}
        {state.phase === "idle" && geminiState.phase === "idle" && digitalState.phase === "idle" && (
          <section className="analytics-grid" aria-label="Analysis capabilities">
            {[
              {
                icon: <BrainCircuit size={22} aria-hidden="true" />,
                title: "Choose the right prompt",
                body: "Guest compact previews the workflow. BYOK short form is the default for saved keys. BYOK long form is the deepest report path.",
              },
              {
                icon: <Activity size={22} aria-hidden="true" />,
                title: "Match the lane to the asset",
                body: "Use Digital Asset Trader for trade setup work, TradFi Research for equities and macro, and Digital Assets Research for tokens and crypto fundamentals.",
              },
              {
                icon: <Download size={22} aria-hidden="true" />,
                title: "Archive and Best Of",
                body: "Finished reports can be downloaded, printed, emailed, archived, and curated into a polished showcase.",
              },
            ].map(({ icon, title, body }) => (
              <article key={title} className="analytics-panel">
                {icon}
                <h2>{title}</h2>
                <p>{body}</p>
              </article>
            ))}
          </section>
        )}
      </main>
      <Footer />
      </div>
    </>
  );
};

export default Analytics;
